/**
 * Cloned from Shell App https://github.com/MGMResorts/web-mfe-shell/blob/main/src/common/context/FlagsContext.tsx
 */
import { Environment, fetchUserKey, FlagsSdk } from '@mgmresorts/client-feature-flagging';
import React, { useState, useEffect, ReactNode, useMemo, useCallback } from 'react';

import { useAuthContext } from '../../utils/auth/AuthProvider';
import { actually } from '../../utils/logging';

import { useFeatureFlagsValue } from './FlagsValueContext';

export type Flags = Awaited<ReturnType<(typeof FlagsSdk)['fetch']>>;
export type UserKey = Awaited<ReturnType<typeof fetchUserKey>> | undefined;

interface FlagsProviderProps {
  children: ReactNode;
}

interface FlagsData {
  flags: Flags;
  isFulfilled: boolean;
}
interface FlagType {
  __typename?: 'FeatureFlagVariationDetail';
  flagKey: string;
  flagValue?: string;
  userKey: string;
}

export interface FlagsContextT {
  flags: Flags;
  getFlagByKey: (key: string) => NonNullable<FlagType>['flagValue'];
  userKey: UserKey;
}

export const FlagsContext = React.createContext<FlagsContextT>({
  flags: [],
  getFlagByKey: () => undefined,
  userKey: undefined,
});

export const FlagsProvider = ({ children }: FlagsProviderProps) => {
  const { acquireTransientToken } = useAuthContext();
  const { setFlags } = useFeatureFlagsValue();

  const [flagsData, setFlagsData] = useState<FlagsData>({ flags: [], isFulfilled: false });
  const [userKey, setUserKey] = useState<UserKey>('');
  const [initializedSDK, setInitializedSDK] = useState(false);

  // Handle userKey fetching.
  useEffect(() => {
    const fetchUserKeyAsync = async () => {
      try {
        const userKey = await fetchUserKey();
        setUserKey(userKey);
      } catch (err) {
        actually.error(err);
      }
    };

    fetchUserKeyAsync().catch(e => actually.error(e));
  }, []);

  // Handle SDK initialization.
  useEffect(() => {
    if (userKey) {
      const environment = process.env.REACT_APP_ENVIRONMENT?.toUpperCase() as Environment;
      const name = 'mgm-identity-oidc-app';

      const fetchToken = async () => {
        const authToken = await acquireTransientToken();

        if (authToken) {
          FlagsSdk.init({
            authToken: authToken?.accessToken,
            environment: environment === 'DEV' ? 'UAT' : environment ?? 'PROD',
            name,
            userKey,
          });
          setInitializedSDK(true);
        }
      };

      fetchToken();
    }
  }, [userKey, acquireTransientToken]);

  // Handle flag fetching when auth and userKey are ready.
  useEffect(() => {
    const fetchFlagsAsync = async () => {
      try {
        if (initializedSDK) {
          const fetchedFlags = await FlagsSdk.fetch();
          setFlagsData({ flags: fetchedFlags, isFulfilled: true });
          setFlags(fetchedFlags);
        }
      } catch (err) {
        actually.error(err);
        // setFlagsData({ flags: [], isFulfilled: true });
        // setFlags([]);
      }
    };

    fetchFlagsAsync().catch(e => actually.error(e));
  }, [initializedSDK, setFlags]);

  const getFlagByKey: FlagsContextT['getFlagByKey'] = useCallback(
    (key: string) => flagsData.flags?.find(x => x?.flagKey === key)?.flagValue,
    [flagsData.flags],
  );

  const value: FlagsContextT = useMemo(() => {
    return {
      flags: flagsData.flags,
      getFlagByKey,
      userKey,
    };
  }, [flagsData, getFlagByKey, userKey]);

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
};

export const useFeatureFlags = () => {
  const context = React.useContext(FlagsContext);

  if (context === undefined) {
    throw new Error('useFlags must be used within a FlagsProvider');
  }

  return context;
};

export enum FEATURE_FLAGS {
  OKTA_IDX_API = 'IDENT-9479_enable-okta-idx-api_2026-01-21_temporary',
  ENABLE_MFA = 'IDENT-9543_enable-mfa_2026-01-28_temporary',
  ENABLE_PASSKEY = 'IDENT-10035_enable-passkey_2026-03-23_temporary',
}
